import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, ArrowLeft, Grid, LayoutGrid } from 'lucide-react';
import { Link } from 'react-router-dom';

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  wedding: string;
  collection: string;
}

// All wedding collections
const weddingCollections = [
  {
    id: 'wedding-01',
    title: 'Elegant Moments',
    location: 'Mumbai',
    images: [
      'IMG_6991.webp', 'IMG_6996.webp', 'IMG_7003.webp', 'IMG_7005.webp',
      'IMG_7076.webp', 'IMG_7078.webp', 'IMG_7083.webp', 'IMG_7092.webp',
      'IMG_7094.webp', 'IMG_7098.webp', 'IMG_7100.webp', 'IMG_7101.webp',
      'IMG_7102.webp', 'IMG_7113.webp'
    ]
  },
  {
    id: 'wedding-02',
    title: 'Royal Celebration',
    location: 'Jaipur',
    images: [
      '1F2A3862.webp', '1F2A3885.webp', '1F2A3889.webp', '1F2A3895.webp',
      '1F2A3896.webp', '1F2A3902.webp', '1F2A3906.webp', '1F2A3908.webp',
      '1F2A3919.webp', '1F2A3951.webp', '1F2A3979.webp', '1F2A3983.webp',
      '1F2A3984.webp', '1F2A3986.webp', '1F2A3988.webp', '1F2A3993.webp',
      '1F2A4001.webp', '1F2A4010.webp', '1F2A4042.webp', '1F2A4052.webp',
      '1F2A4056.webp', '1F2A4070.webp'
    ]
  },
  {
    id: 'wedding-03',
    title: 'Timeless Love',
    location: 'Udaipur',
    images: [
      '1F2A0059.webp', '1F2A0083.webp', '1F2A0336.webp', '1F2A0462.webp',
      '1F2A0504.webp', '1F2A0514.webp', '1F2A0518.webp', '1F2A0520.webp',
      '1F2A0532.webp', '1F2A0533.webp', '1F2A0570.webp', '1F2A0571.webp',
      '1F2A0852.webp', '1F2A0864.webp', '1F2A1062.webp', '1F2A1104.webp',
      '1F2A1298.webp', '1F2A1309.webp'
    ]
  },
  {
    id: 'wedding-04',
    title: 'Grand Heritage',
    location: 'Jodhpur',
    images: [
      '1F2A0134.webp', '1F2A0150.webp', '1F2A0201.webp', '1F2A0793.webp',
      '1F2A1062.webp', '1F2A1377.webp', '1F2A1387.webp', '1F2A1398.webp',
      '1F2A1424.webp', '1F2A1441.webp', '1F2A1470.webp', '1F2A1484_1.webp',
      '1F2A1521.webp', '1F2A1604.webp', '1F2A1879.webp', '1F2A1920.webp',
      '1F2A1954.webp', '1F2A1971.webp', '1F2A1980.webp', '1F2A2185.webp',
      '1F2A2186.webp', '1F2A2254.webp', '1F2A2283.webp', '1F2A2287.webp',
      '1F2A2296.webp', '1F2A2322.webp', '1F2A2341.webp', '1F2A2379.webp',
      '1F2A2451.webp', '1F2A2626.webp', '1F2A2693.webp', '1F2A2721.webp',
      '1F2A2732.webp', '1F2A2744.webp', '1F2A2966.webp', '1F2A8171.webp',
      '1F2A8211.webp', '1F2A8236.webp', '1F2A8265_1.webp', '1F2A8287.webp',
      '1F2A8534.webp'
    ]
  }
];

// Build all images from collections
const buildAllImages = (): GalleryImage[] => {
  let id = 1;
  const images: GalleryImage[] = [];
  
  weddingCollections.forEach(collection => {
    collection.images.forEach(img => {
      images.push({
        id: id++,
        src: `/gallery/${collection.id}/${img}`,
        alt: `${collection.title} - ${img}`,
        wedding: collection.title,
        collection: collection.id
      });
    });
  });
  
  return images;
};

const allImages = buildAllImages();

export const GalleryPage: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filter, setFilter] = useState<string>('all');
  const [layout, setLayout] = useState<'masonry' | 'grid'>('masonry');
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  const filteredImages = useMemo(() => {
    if (filter === 'all') return allImages;
    return allImages.filter(img => img.collection === filter);
  }, [filter]);

  const openLightbox = useCallback((image: GalleryImage, index: number) => {
    setSelectedImage(image);
    setCurrentIndex(index);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  }, []);

  const goToPrevious = useCallback(() => {
    const newIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  }, [currentIndex, filteredImages]);

  const goToNext = useCallback(() => {
    const newIndex = currentIndex === filteredImages.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  }, [currentIndex, filteredImages]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, goToPrevious, goToNext, closeLightbox]);

  const handleImageLoad = (id: number) => {
    setLoadedImages(prev => new Set(prev).add(id));
  };

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-[1800px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-stone-600 hover:text-stone-900 transition-colors">
            <ArrowLeft size={20} />
            <span className="text-sm uppercase tracking-widest">Back</span>
          </Link>
          
          <h1 className="font-serif text-2xl text-stone-900">Full Gallery</h1>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => setLayout('masonry')}
              className={`p-2 rounded ${layout === 'masonry' ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'}`}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setLayout('grid')}
              className={`p-2 rounded ${layout === 'grid' ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'}`}
            >
              <Grid size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Filter Bar */}
      <div className="pt-24 pb-8 px-6 bg-white border-b border-stone-200">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-2 text-xs uppercase tracking-widest transition-all ${
                filter === 'all' 
                  ? 'bg-stone-900 text-white' 
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              All ({allImages.length})
            </button>
            {weddingCollections.map(collection => (
              <button
                key={collection.id}
                onClick={() => setFilter(collection.id)}
                className={`px-6 py-2 text-xs uppercase tracking-widest transition-all ${
                  filter === collection.id 
                    ? 'bg-stone-900 text-white' 
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {collection.title} ({collection.images.length})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      <main className="max-w-[1800px] mx-auto px-6 py-12">
        {layout === 'masonry' ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
            {filteredImages.map((image, index) => (
              <div
                key={image.id}
                className="break-inside-avoid mb-4 group cursor-pointer relative overflow-hidden"
                onClick={() => openLightbox(image, index)}
              >
                {!loadedImages.has(image.id) && (
                  <div className="absolute inset-0 bg-stone-200 animate-pulse" />
                )}
                <img
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  onLoad={() => handleImageLoad(image.id)}
                  className={`w-full h-auto object-cover transition-all duration-500 group-hover:scale-105 ${
                    loadedImages.has(image.id) ? 'opacity-100' : 'opacity-0'
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-white text-sm font-light">{image.wedding}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredImages.map((image, index) => (
              <div
                key={image.id}
                className="aspect-square group cursor-pointer relative overflow-hidden"
                onClick={() => openLightbox(image, index)}
              >
                {!loadedImages.has(image.id) && (
                  <div className="absolute inset-0 bg-stone-200 animate-pulse" />
                )}
                <img
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  onLoad={() => handleImageLoad(image.id)}
                  className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${
                    loadedImages.has(image.id) ? 'opacity-100' : 'opacity-0'
                  }`}
                />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/30 transition-colors duration-300" />
              </div>
            ))}
          </div>
        )}
        
        {/* Image count */}
        <div className="text-center mt-12 text-stone-500 text-sm">
          Showing {filteredImages.length} of {allImages.length} images
        </div>
      </main>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-stone-950/98 backdrop-blur-lg flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors z-[110]"
            onClick={closeLightbox}
          >
            <X size={32} strokeWidth={1} />
          </button>

          <button
            className="absolute left-4 md:left-8 text-white/30 hover:text-white transition-colors p-2"
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
          >
            <ChevronLeft size={48} strokeWidth={1} />
          </button>

          <div
            className="max-w-[90vw] max-h-[85vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-[85vh] object-contain shadow-2xl"
            />
            <div className="absolute -bottom-16 left-0 right-0 text-center">
              <p className="text-white/80 font-serif text-xl">{selectedImage.wedding}</p>
              <p className="text-white/40 text-sm mt-2">{currentIndex + 1} / {filteredImages.length}</p>
            </div>
          </div>

          <button
            className="absolute right-4 md:right-8 text-white/30 hover:text-white transition-colors p-2"
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
          >
            <ChevronRight size={48} strokeWidth={1} />
          </button>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
